// How a registration writes the spine's display_name.
//
// first_name and last_name are the boxes on the form. The event still carries
// display_name so existing reports keep working. Compose here, once, so the
// phone, the tablet and the server cannot disagree.

import type { FieldDefinition, FieldValue, FieldValues } from "./types.js";

function textOf(value: FieldValue | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * first + last when either is present, otherwise a leftover display_name box.
 *
 * Never invents a third name. An empty result is null so the slot stays empty
 * rather than becoming a space.
 */
export function composeDisplayName(
  fields: FieldValues,
  fallback: string | null = null,
): string | null {
  const first = textOf(fields["first_name"]);
  const last = textOf(fields["last_name"]);
  const joined = [first, last].filter((part) => part !== "").join(" ");
  if (joined !== "") return joined;
  const named = textOf(fields["display_name"]);
  if (named !== "") return named;
  if (typeof fallback === "string" && fallback.trim() !== "") return fallback.trim();
  return null;
}

/**
 * A published instruction that still has one "Your name" box.
 *
 * first_name and last_name are what the form draws. Older shows recorded
 * display_name. Those two facts have to meet without a republish, or a stand
 * that was set up before the split keeps asking for one name forever.
 */
export function instructionWantsLegacyDisplayName(fields: readonly FieldDefinition[]): boolean {
  return (
    fields.some((field) => field.field_id === "display_name") &&
    !fields.some((field) => field.field_id === "first_name")
  );
}

/** Turn a leftover display_name box into first and last, in that place in the list. */
export function presentNameFields(fields: readonly FieldDefinition[]): FieldDefinition[] {
  const hasFirst = fields.some((field) => field.field_id === "first_name");
  const hasLast = fields.some((field) => field.field_id === "last_name");
  const out: FieldDefinition[] = [];
  for (const field of fields) {
    if (field.field_id !== "display_name") {
      out.push(field);
      continue;
    }
    const required = field.required === true;
    const max_length =
      field.type === "text" && typeof field.max_length === "number" ? field.max_length : 80;
    if (!hasFirst) {
      out.push({
        field_id: "first_name",
        type: "text",
        label: "First name",
        required,
        max_length,
      });
    }
    if (!hasLast) {
      out.push({
        field_id: "last_name",
        type: "text",
        label: "Last name",
        required,
        max_length,
      });
    }
  }
  return out;
}

/**
 * Answers an older instruction will accept: one display_name, not two new keys.
 *
 * A silently extra first_name is a field the register route refuses. Folding
 * here is what lets the two boxes exist in front of a bundle that never named
 * them.
 */
export function foldNameAnswers(values: FieldValues): FieldValues {
  if (textOf(values["first_name"]) === "" && textOf(values["last_name"]) === "") return values;
  const next: Record<string, FieldValue> = { ...values };
  delete next["first_name"];
  delete next["last_name"];
  const named = composeDisplayName(values);
  if (named !== null) next["display_name"] = named;
  return next;
}
